import React from 'react';
import { Switch, Route } from 'react-router-dom';

import request from '~/src/util/request';
import { useResource } from '~/src/util/useResource';
import { Button, ButtonBar } from '~/src/lib/Buttons';
import { ProtoEditor, useEditor, getEditorActions } from '~/src/lib/ProtoEditor';
import { Page } from '~/src/lib/Page';
import { TablePage } from '~/src/lib/Table';

const useTags = () => useResource('/v0/knowledge/tags/lookups');
const useSingleTag = (rawId) => {
	const { isLoading, error, refresh, data: allTags } = useTags();
	const tagId = parseInt(rawId, 10);
	const tag = allTags?.find((tag) => tag.id === tagId);
	return {
		isLoading,
		error,
		refresh,
		tag,
		tagId,
	};
};

const TAGS_HEADERS = [{
	label: 'Tag',
	key: 'name',
	isWide: true
}, {
	label: 'Lookup count',
	key: 'count',
	renderItem: (item) => item.lookups.length,
}, {
	key: 'actions',
	renderItem: (item) => (
		<ButtonBar>
			<Button to={`/knowledge/tags/${item.id}/lookups/new`}>Add</Button>
			<Button variant="neutral" to={`/knowledge/tags/${item.id}/lookups`}>Edit</Button>
		</ButtonBar>
	)
}];

const TagsLookupHome = () => {
	const { isLoading, error, data } = useTags();
	return (
		<TablePage
			keyProp="id"
			headers={TAGS_HEADERS}
			items={data}
			title="Tags lookup"
			isLoading={isLoading}
			error={error}
		/>
	);
};

const TAG_LOOKUPS_HEADERS = [{
	label: 'Lookup text',
	key: 'text',
	isWide: true
}, {
	key: 'edit',
	renderItem: (item, aux) => (
		<Button variant="danger" onClick={() => aux.removeItem(item)}>Delete</Button>
	)
}];

const SingleTagLookup = ({ match }) => {
	const { isLoading, error, tag, tagId, refresh } = useSingleTag(match.params.id);
	const removeItem = async (text) => {
		await request('DELETE', `/v0/knowledge/tags/${tagId}/lookups/${encodeURIComponent(text)}`);
		refresh();
	};
	return (
		<TablePage
			backLink="/knowledge/tags"
			headers={TAG_LOOKUPS_HEADERS}
			items={tag?.lookups}
			title={`Lookups for tag ${tag?.name}`}
			isLoading={isLoading}
			error={error}
			headerItemAux={{ removeItem }}
			actions={<Button to={`/knowledge/tags/${tagId}/lookups/new`}>Add new</Button>}
		/>
	);
};

const EDITOR_FIELDS = [{
	name: 'text',
	label: 'Text',
	type: 'text',
	autoFocus: true
}];

const NewTagLookup = ({ match }) => {
	const { isLoading, error, tag, tagId, refresh } = useSingleTag(match.params.id);
	const editor = useEditor({
		endpoint: `/v0/knowledge/tags/${tagId}/lookups`,
		fields: EDITOR_FIELDS,
		isNew: true,
		newItem: { id: tagId },
		// We pass in our own refresh handler as the resource we're loading
		// the tags from is not the same as the resource we're posting to
		refresh,
	});
	return (
		<Page
			title={`Add lookup for "${tag?.name}"`}
			actions={getEditorActions(editor)}
			isLoading={isLoading}
			error={error}
		>
			<ProtoEditor key={tagId} editor={editor} />
		</Page>
	);
};

export const TagsLookup = () => (
	<Switch>
		<Route exact path="/knowledge/tags/:id/lookups" component={SingleTagLookup} />
		<Route exact path="/knowledge/tags/:id/lookups/new" component={NewTagLookup} />
		<Route component={TagsLookupHome} />
	</Switch>
);